import { authApi } from '@oneview/api/authApi';
import { settingApi } from '@oneview/api/settingApi';
import flagsData from '@oneview/config/featureFlags/flags.json';
import { ExtendedFeatureFlags } from '@oneview/config/featureFlags/types';
import {
    DashboardChartId,
    DashboardMainSectionId,
    DashboardSummaryTableId,
    MarketOverviewTableId,
    PositionsSectionId,
    SectorSettingsTableId,
    SideBarComponentId,
    SideBarTabId,
    TopPositionsChartId,
} from '@oneview/constants/ids';
import {
    AdvisorSettingsScope,
    ClientSettingScope,
    CommonSettingsScope,
    SettingsScope,
} from '@oneview/constants/lookups';
import { Settings } from '@oneview/types/settings';
import { mergeOverwriteArray } from '@orbis-systems/orbis-react-toolkit';
import { createReducer } from '@reduxjs/toolkit';
import { saveSettingsLocal } from './settingActions';

const featureFlags: ExtendedFeatureFlags = flagsData as unknown as ExtendedFeatureFlags;

export const SETTING_REDUCER_DEFAULT_STATE: Settings = {
    [SettingsScope.Common]: {
        [CommonSettingsScope.Global]: {
            showMainMenu: true,
            showNewsfeed: true,
            sideBarSections: Object.values(SideBarComponentId).map((item, index) => ({
                id: item,
                order: index,
                disabled: item === SideBarComponentId.MarketClock,
            })),
            minimizeNewsfeed: false,
            useDarkTheme: true,
            showSidebar: true,
            groupUpTabs: false,
            showQuoteRecentNews: true,
            idleTimeout: 1800000,
            rowsPerPage: 10,
            defaultTabSectionId: SideBarTabId.Heatmap,
            defaultRebalanceThreshold: 10,
            defaultBaseCurrency: 'USD',
        },
    },
    [SettingsScope.Advisor]: {
        [AdvisorSettingsScope.Dashboard]: {
            charts: Object.values(DashboardChartId).map((item, index) => ({
                id: item,
                order: index,
                disabled: false,
            })),
            tables: Object.values(DashboardSummaryTableId).map((item, index) => ({
                id: item,
                order: index,
                disabled: false,
            })),
            mainSections: Object.values(DashboardMainSectionId).map((item, index) => ({
                id: item,
                order: index,
                disabled: false,
            })),
        },
        [AdvisorSettingsScope.UserProfile]: {
            address: '',
            firstName: '',
            phoneNumber: '',
            email: '',
            additionalInfo: '',
        },
        [AdvisorSettingsScope.MarketOverview]: {
            showNews: true,
            showTables: true,
            showGraphs: true,
            showTipRank: true,
            tables: Object.values(MarketOverviewTableId).map((item, index) => ({
                id: item,
                order: index,
                disabled: false,
            })),
        },

        [AdvisorSettingsScope.EtfCenter]: {
            showNews: true,
        },

        [AdvisorSettingsScope.IpoCenter]: {
            showNews: true,
        },

        [AdvisorSettingsScope.Sectors]: {
            showNews: true,
            showTablesCharts: true,
            tables: Object.values(SectorSettingsTableId).map((item, index) => ({
                id: item,
                order: index,
                disabled: false,
            })),
        },
        [AdvisorSettingsScope.Earnings]: {
            showNews: true,
        },
        [AdvisorSettingsScope.StockScreener]: {},
        [AdvisorSettingsScope.AnalystRating]: {},
        [AdvisorSettingsScope.PoppedOutTabs]: { savedTabs: [] },
        [AdvisorSettingsScope.Positions]: {
            positionsSections: Object.values(PositionsSectionId).map((item, index) => ({
                id: item,
                order: index,
                disabled: false,
            })),
            charts: Object.values(TopPositionsChartId).map((item, index) => ({
                id: item,
                order: index,
                disabled: false,
            })),
        },
    },
    [SettingsScope.Client]: {
        [ClientSettingScope.General]: {
            defaultAccountId: 0,
        },
        [ClientSettingScope.Dashboard]: {
            accountsOrder: [],
        },
    },
    [SettingsScope.Middleware]: {
        BranchLogoDark: '',
        BranchLogoLight: '',
        BranchName: '',
        ShowBranchName: true,
        AllowClientTrading: false,
        AllowRtbs: true,
        AllowIav: true,
        AllowTransfers: true,
        AllowDocuments: true,
        AddIavProcessToken: true,
        AllowNotionalOrder: true,
        OnboardingUrl: '',
        RequireNbbo: true,
        HideFeedbackButton: false,
        HideTableIds: { values: [] },
        HideAdvisorRouteIds: { values: [] },
        HideClientRouteIds: { values: [] },
        HideChartIds: { values: [] },
        HideTableColumnIds: { values: [] },
        HidePageTabs: {
            disableAccountDetailsTabIds: [],
            disableTradingTabIds: [],
            disableQuoteDetailsTabIds: [],
        },
        HideAlgoOrderTypes: { values: [] },
        EditCommissionAllowed: false,
        FeatureFlags: featureFlags,
        ClientCanViewDocuments: false,
        ClientCanUploadDocuments: false,
        ClientCanDeleteDocuments: false,
        EnableProfileSettingsNameEditing: false,
        AllowCrmProfileView: true,
        EnableSettlementCurrency: true,
        EnablePostAllocation: true,
        EnableStockPriceDelayInfo: true,
        EnableTradeDestinationModel: true,
        EnableTradeDestinationAveragePriceAccount: true,
        EnableFirstAccountAsDefaultInTrading: false,
        EnableAlgoTradingNonUsMarkets: false,
        EnableSpvPreIpoMarketplace: false,
    },
    [SettingsScope.Table]: {},
    [SettingsScope.TradeBlotter]: {},
    [SettingsScope.Model]: {},
    [SettingsScope.Account]: {},
};

const settingReducer = createReducer(SETTING_REDUCER_DEFAULT_STATE, (builder) => {
    builder.addCase(saveSettingsLocal, (state, { payload }) => {
        mergeOverwriteArray(state, payload);

        // remove deleted blotters
        const blotterKeys = Object.keys(payload.tradeBlotter || {});
        for (const key of blotterKeys) {
            if (payload.tradeBlotter?.[key] === undefined) {
                delete state.tradeBlotter[key];
            }
        }

        // replacing default rebalance thresholds instead of merging
        const modelSettings = payload.model;
        if (modelSettings) {
            for (const modelId of Object.keys(modelSettings)) {
                state.model = {
                    ...state.model,
                    [modelId]: modelSettings[Number(modelId)],
                };
            }
        }
    });

    builder.addMatcher(settingApi.endpoints.restoreSettings.matchFulfilled, (state, action) => {
        mergeOverwriteArray(state, SETTING_REDUCER_DEFAULT_STATE);
        mergeOverwriteArray(state, action.payload);
    });

    builder.addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        state.tradeBlotter = {};
        state.table = {};
        state.middleware = SETTING_REDUCER_DEFAULT_STATE.middleware;
    });
});

export { settingReducer };
